import React, { useMemo } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Text from "components/atoms/Text";
import { COLOR_SYSTEM } from "components/design/design-system";
import { RootState } from "store";
import { NhisTransactionStatus } from "types/nhis";
import NhisRequestStatusList from "components/organisms/NhisRequestStatusList/NhisRequestStatusList";
import { ACTIVE_STATUSES } from "components/organisms/NhisRequestStatusList/nhisStatus";
import SectionHeader from "./SectionHeader";

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-radius: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const SingleColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DashboardNhisSection: React.FC = () => {
  const navigate = useNavigate();
  const all = useSelector(
    (state: RootState) => state.data.nhisRecentRequests,
  );

  const activeRequests = useMemo<NhisTransactionStatus[]>(
    () =>
      (all ?? []).filter((r) => ACTIVE_STATUSES.includes(r.status)),
    [all],
  );

  const recentDelivered = useMemo<NhisTransactionStatus[]>(
    () =>
      (all ?? [])
        .filter((r) => r.status === "DELIVERED")
        .sort((a, b) => {
          const aAt = a.deliveredAt ?? a.createdAt;
          const bAt = b.deliveredAt ?? b.createdAt;
          return new Date(bAt).getTime() - new Date(aAt).getTime();
        }),
    [all],
  );

  // 3.4 조건부 표시 — 둘 다 0건이면 섹션 전체 숨김
  if (activeRequests.length === 0 && recentDelivered.length === 0) {
    return null;
  }

  const handleItemClick = (request: NhisTransactionStatus) => {
    if (request.patientId) {
      navigate(`/patients/${request.patientId}`);
    }
  };

  const showBoth = activeRequests.length > 0 && recentDelivered.length > 0;

  const activeBlock = (
    <SingleColumn>
      <SubHeading>
        <Text textType="Body1SemiBold" color={COLOR_SYSTEM.get("Gray")?.[800]}>
          진행 중 ({activeRequests.length}건)
        </Text>
      </SubHeading>
      <NhisRequestStatusList
        requests={activeRequests}
        variant="compact"
        onItemClick={handleItemClick}
        hideHeader
      />
    </SingleColumn>
  );

  const deliveredBlock = (
    <SingleColumn>
      <SubHeading>
        <Text textType="Body1SemiBold" color={COLOR_SYSTEM.get("Gray")?.[800]}>
          최근 완료 ({recentDelivered.length}건)
        </Text>
      </SubHeading>
      <NhisRequestStatusList
        requests={recentDelivered}
        variant="compact"
        onItemClick={handleItemClick}
        hideHeader
      />
    </SingleColumn>
  );

  return (
    <Card>
      <SectionHeader
        title="처방내역 조회 현황"
        description="최근 2일 동안의 조회 요청 현황이에요"
      />

      {showBoth ? (
        <Columns>
          {activeBlock}
          {deliveredBlock}
        </Columns>
      ) : activeRequests.length > 0 ? (
        activeBlock
      ) : (
        deliveredBlock
      )}
    </Card>
  );
};

export default DashboardNhisSection;
